/*
  Full-screen product gallery lightbox — Swiper Zoom + Thumbs modules.

  Swiper (40KB gzipped plus its CSS) only serves this dialog, so neither file
  is on the page until the lightbox is first opened; pointing at or focusing a
  gallery image starts the download a moment early. Instances are keyed by
  dialog, so a section re-rendered in the theme editor simply builds afresh.
*/
(() => {
  'use strict';

  if (window.psjLightbox) return;
  window.psjLightbox = true;

  const instances = new WeakMap();
  let swiperPromise = null;

  const loadSwiper = (dialog) => {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (swiperPromise) return swiperPromise;

    const { swiperJs, swiperCss } = dialog.dataset;
    swiperPromise = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = swiperCss;
      const cssReady = new Promise((done) => {
        css.onload = done;
        css.onerror = done;
      });
      document.head.appendChild(css);

      const script = document.createElement('script');
      script.src = swiperJs;
      script.async = true;
      script.onload = () =>
        cssReady.then(() => (window.Swiper ? resolve(window.Swiper) : reject(new Error('Swiper did not register'))));
      script.onerror = () => {
        swiperPromise = null;
        reject(new Error('Swiper failed to load'));
      };
      document.head.appendChild(script);
    });
    return swiperPromise;
  };

  const updateCounter = (dialog, main) => {
    const counter = dialog.querySelector('[data-lightbox-counter]');
    if (counter && main) counter.textContent = `${main.activeIndex + 1} / ${main.slides.length}`;
  };

  const build = (dialog, Swiper) => {
    const thumbs = new Swiper(dialog.querySelector('[data-lightbox-thumbs]'), {
      slidesPerView: 'auto',
      spaceBetween: 10,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      centerInsufficientSlides: true,
      freeMode: true,
      // The strip is aria-hidden (pointer shortcut only), so no ARIA from Swiper.
      a11y: { enabled: false },
    });

    const main = new Swiper(dialog.querySelector('[data-lightbox-main]'), {
      spaceBetween: 24,
      zoom: { maxRatio: 3 },
      thumbs: { swiper: thumbs },
      keyboard: { enabled: true },
      navigation: {
        prevEl: dialog.querySelector('.carousel__arrow--prev'),
        nextEl: dialog.querySelector('.carousel__arrow--next'),
      },
      // The wrapper is a plain div, not a list, so slides stay role="group"
      // (each labelled "n / total") — no list/listitem mismatch for axe.
      a11y: { slideRole: 'group' },
      on: {
        slideChange: (swiper) => {
          swiper.zoom.out();
          updateCounter(dialog, swiper);
        },
      },
    });

    return { main, thumbs };
  };

  const dialogFor = (trigger) =>
    trigger.closest('[data-main-product]')?.querySelector('[data-product-lightbox]') ||
    document.querySelector('[data-product-lightbox]');

  const open = async (trigger) => {
    const dialog = dialogFor(trigger);
    if (!dialog) return;
    const index = parseInt(trigger.dataset.lightboxIndex, 10) || 0;

    if (!dialog.open) dialog.showModal();

    let instance = instances.get(dialog);
    if (!instance) {
      dialog.classList.add('is-loading');
      try {
        instance = build(dialog, await loadSwiper(dialog));
        instances.set(dialog, instance);
      } catch (error) {
        console.error(error);
        // Still usable without Swiper: native horizontal scrolling, no zoom.
        dialog.classList.add('is-fallback');
        const slide = dialog.querySelectorAll('[data-lightbox-main] .swiper-slide')[index];
        slide?.scrollIntoView({ block: 'nearest', inline: 'center' });
        return;
      } finally {
        dialog.classList.remove('is-loading');
      }
    }

    instance.main.update();
    instance.thumbs.update();
    instance.main.slideTo(index, 0);
    updateCounter(dialog, instance.main);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-lightbox-trigger]');
    if (!trigger) return;
    open(trigger);
  });

  // Warm the cache as soon as someone reaches for an image.
  const warm = (event) => {
    const trigger = event.target.closest?.('[data-lightbox-trigger]');
    if (!trigger) return;
    const dialog = dialogFor(trigger);
    if (dialog) loadSwiper(dialog).catch(() => {});
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);

  document.addEventListener('click', (event) => {
    const dialog = event.target.closest('[data-product-lightbox]');
    if (!dialog) return;
    const instance = instances.get(dialog);
    if (event.target.closest('[data-lightbox-zoom-in]')) instance?.main.zoom.in();
    if (event.target.closest('[data-lightbox-zoom-out]')) instance?.main.zoom.out();
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target.matches?.('[data-product-lightbox]')) instances.get(event.target)?.main.zoom.out();
    },
    true
  );

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-product-lightbox]').forEach((dialog) => {
      const instance = instances.get(dialog);
      instance?.main.destroy(true, true);
      instance?.thumbs.destroy(true, true);
      instances.delete(dialog);
    });
  });
})();
